<template>
    <div
        :key="snackbarId"
        class="sbs-snackbar trs"
        :class="snackbarClasses"
        :style="dimensionStyles"
        role="alert">
        <!-- @slot Слот перед контентом -->
        <slot name="prepend">
            <div
                v-if="iconName"
                class="sbs-snackbar__icon-wrapper">
                <div
                    class="sbs-snackbar__icon-background"
                    :class="iconClasses"></div>
                <SbsIcon
                    class="sbs-snackbar__icon"
                    :class="iconClasses"
                    :iconName="iconName"
                    :iconGroup="iconGroup"
                    :iconSize="iconSize" />
            </div>
        </slot>

        <div class="sbs-snackbar__content">
            <!-- @slot Слот по-умолчанию -->
            <slot>
                <div
                    v-html="message"
                    class="sbs-snackbar__message"></div>
            </slot>
        </div>

        <!-- @slot Слот для крестика -->
        <slot name="closeButton">
            <SbsButtonIcon
                v-if="showCloseButton"
                class="sbs-snackbar__close-button"
                v-bind="closeButtonData"
                @click="$emit('close')" />
        </slot>
    </div>
</template>

<script>
import { isArrayContainsValue } from '../../utils/validators.js';
import {
    AllSizes,
    APP_SIZES,
    SNACKBAR_SIZES,
} from '../../utils/sizes.js';
import { AllColors } from '../../utils/colors.js';
import {
    getRandomId,
} from '../../utils/helpers.js';
import dimensionsMixin from '../../mixins/dimensions.js';
import {
    AllIconGroups,
    ICON_GROUPS,
} from '../icon/utils/consts.js';
import SbsIcon from '../icon/SbsIcon.vue';
import SbsButtonIcon from '../button/SbsButtonIcon.vue';

export default {
    name: 'SbsSnackbar',
    components: {
        SbsButtonIcon,
        SbsIcon,
    },
    mixins: [dimensionsMixin],
    emits: ['close'],
    data() {
        return {
            parentContainer: null,
        };
    },
    computed: {
        snackbarId() {
            return this.id || `sbs-snackbar-${getRandomId()}`;
        },
        closeButtonData() {
            return {
                width: 20,
                height: 20,
                iconName: 'close',
                colorTheme: AllColors.GRAY,
                hoverColor: AllColors.WHITE,
            };
        },
        snackbarClasses() {
            return [
                this.size,
                this.snackbarClass,
            ];
        },
        iconClasses() {
            const classes = [];

            if (this.iconBackgroundTheme) {
                classes.push(`bg-${this.iconBackgroundTheme}`);
            }

            if (this.iconColorTheme) {
                classes.push(`clr-${this.iconColorTheme}`);
            }

            return classes;
        },
    },
    props: {
        /**
         * Тема (цветовые решения) для цвета фона иконки снекбара
         */
        iconBackgroundTheme: {
            type: String,
            default: '',
        },
        /**
         * Тема (цветовые решения) для цвета иконки снекбара
         */
        iconColorTheme: {
            type: String,
            default: '',
        },
        /**
         * Тип иконки
         */
        iconGroup: {
            type: String,
            default: AllIconGroups.SOLID,
            validator: isArrayContainsValue(ICON_GROUPS),
        },
        /**
         * Название иконки из FontAwesome.
         * Список всех иконок можно посмотреть [здесь](https://fontawesome.com/v6/icons)
         */
        iconName: {
            type: String,
            default: '',
        },
        /**
         * Размер иконки
         */
        iconSize: {
            type: String,
            default: AllSizes.MEDIUM,
            validator: isArrayContainsValue(APP_SIZES),
        },
        /**
         * Уникальный идентификатор снекбара
         */
        id: {
            type: [Number, String],
            default: '',
        },
        /**
         * Отображаемое сообщение
         */
        message: {
            type: String,
            default: '',
        },
        /**
         * Отображение кнопки закрытия
         */
        showCloseButton: {
            type: Boolean,
            default: true,
        },
        /**
         * Размер снекбара (`medium`/`large`)
         */
        size: {
            type: String,
            default: AllSizes.MEDIUM,
            validator: isArrayContainsValue(SNACKBAR_SIZES),
        },
        /**
         * Задает внешние отсупы между снекбарами.
         */
        snackbarClass: {
            type: String,
            default: '',
        },
        /**
         * Задает внутренние отступы для контейнера снекбаров.
         */
        snackbarContainerClass: {
            type: String,
            default: '',
        },
    },
};
</script>

<style lang="scss">
.sbs-snackbar-container {
    position: fixed;
    top: 0;
    left: 0;
    z-index: 100;
    display: flex;
    flex-direction: column-reverse;
    align-items: center;
    width: var(--vw, 100vw);
    height: var(--vh, 100vh);
    padding: 20px;
    overflow: hidden;
    pointer-events: none;

    &--top,
    &--top-start,
    &--top-end {
        flex-direction: column;
    }

    &--top-start,
    &--bottom-start {
        align-items: flex-start;
    }

    &--top-end,
    &--bottom-end {
        align-items: flex-end;
    }
}

.sbs-snackbar {
    position: relative;
    display: inline-flex;
    align-items: flex-start;
    max-width: 100%;
    padding: 12px 20px;
    color: $white;
    background-color: $black;
    border-radius: $rounded-15;
    transition-property: opacity, transform;
    pointer-events: all;

    &.full-width {
        width: 100%;
    }

    &.large {
        padding: 20px;
    }

    &__icon-wrapper {
        position: relative;
        display: flex;
        flex-shrink: 0;
        align-items: center;
        justify-content: center;
        width: 36px;
        height: 36px;
        margin-right: 15px;
        padding: 5px;
        font-size: 16px;
        border-radius: $rounded-circle;

        .sbs-snackbar.large & {
            width: 60px;
            height: 60px;
            padding: 8px;
            font-size: 27px;
        }
    }

    &__icon-background {
        position: absolute;
        top: 0;
        left: 0;
        z-index: 0;
        width: 100%;
        height: 100%;
        border-radius: inherit;
        opacity: 0.25;
    }

    &__icon {
        position: relative;
        z-index: 1;
        width: 100%;
        height: 100%;
        border-radius: inherit;
    }

    &__content {
        align-self: center;
        margin-right: auto;
        word-wrap: break-word;
    }

    &__message {
        @include typo-additional-medium;
    }

    &__close-button {
        flex-shrink: 0;
        margin-left: 35px;
        font-size: 23px;

        .sbs-snackbar.medium & {
            align-self: center;
        }
    }
}
</style>
